import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Customized,
} from 'recharts';
import { STATUS_COLOR, STATUS_LABEL, SEGMENTS, segmentOf } from './bonusStatus';

const SERIES = [
  { key: 'bet_count',       label: 'Bet count',      color: '#4fc3f7' },
  { key: 'stake_eur',       label: 'Turnover',        color: '#d4a843' },
  { key: 'ggr_eur',         label: 'GGR',             color: '#00c896' },
  { key: 'ngr3_eur',        label: 'NGR3',            color: '#a78bfa' },
  { key: 'hold_eur',        label: 'Hold',            color: '#f87171' },
  { key: 'deposit_eur',     label: 'Deposits',        color: '#34d399' },
  { key: 'cashout_eur',     label: 'Cashouts',        color: '#fb923c' },
  { key: 'issued_bonus_eur',label: 'Issued bonuses',  color: '#e879f9' },
];

const STRIP_H = 56;   // bonus strip height in px (same value as BonusTimelineStrip)
const STRIP_GAP = 8;  // gap between strip bottom and plot area top
// Strip is rendered inside the chart SVG top-margin area — expand top margin to fit.
const CHART_MARGIN = { top: STRIP_H + STRIP_GAP, right: 8, bottom: 4, left: 0 };
const LEFT_AXIS_W = 48;
// Chart height keeps the same plot-area height as the original 320px design:
// original: 320 - 12(top) = 308 available; new: CHART_HEIGHT - CHART_MARGIN.top = same
const CHART_HEIGHT = 320 + STRIP_H + STRIP_GAP - 12; // 372

function fmtDay(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function fmtDateFull(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtNum(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1000000) return (n / 1000000).toFixed(2) + 'M';
  if (abs >= 1000) return (n / 1000).toFixed(1) + 'K';
  return Math.round(n).toLocaleString('en-GB');
}

function Toggle({ active, color, onClick, children }) {
  const activeColor = color || '#d4a843';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderColor: active ? activeColor : '#2a3042',
        color: active ? activeColor : '#7a8499',
        backgroundColor: active ? activeColor + '18' : 'transparent',
      }}
      className="px-2.5 py-1 text-[11px] cursor-pointer rounded border select-none inline-flex items-center gap-1"
    >
      {children}
    </button>
  );
}

function ChartTooltip({ active, payload, visible }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-[#0d0f12] border border-[#2a3042] rounded p-2 text-[11px]">
      <p className="text-[11px] font-bold text-[#d8dde8] mb-1">
        {row.week} (d{row.dayIdx})
      </p>
      {SERIES.filter((s) => visible[s.key]).map((s) => (
        <div key={s.key} style={{ color: s.color }}>
          {s.label}: {fmtNum(row[s.key])}
        </div>
      ))}
      <div className="text-[#e0a23c] mt-1">
        Bonus: {row.bonus_count} issued · €{fmtNum(row.bonus_value_eur)}
      </div>
    </div>
  );
}

// Diamond marker rendered inside SVG at the top of each reference line.
function OverlayMarker({ viewBox, ol, onClick, isSelected }) {
  const { x } = viewBox || {};
  if (x == null) return null;
  return (
    <g style={{ cursor: 'pointer' }} onClick={onClick}>
      <rect x={x - 8} y={-2} width={16} height={22} fill="transparent" />
      <polygon
        points={`${x},1 ${x + 5},8 ${x},15 ${x - 5},8`}
        fill={ol.color} opacity={isSelected ? 1 : 0.85}
        stroke={isSelected ? '#fff' : 'none'} strokeWidth={1}
      />
    </g>
  );
}

// Returns "YYYY-Www" ISO week key so days in the same Mon–Sun week share a key.
function isoWeekKey(dateStr) {
  const d = new Date(dateStr);
  const dow = d.getDay() || 7; // 1=Mon … 7=Sun
  d.setDate(d.getDate() + 4 - dow); // shift to Thursday of the same week
  const year = d.getFullYear();
  const jan1 = new Date(year, 0, 1);
  const weekNum = Math.ceil(((d - jan1) / 86400000 + 1) / 7);
  return `${year}-W${weekNum}`;
}

const DOW_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Custom X-axis tick that shows day-of-week context when zoomed in.
function CustomXTick({ x, y, payload, dowMap, visibleDays }) {
  const val = payload?.value;
  const dow = dowMap?.get(val); // 0=Sun … 6=Sat
  const isMonday = dow === 1;

  if (visibleDays > 60 || dow == null) {
    // Default: just the date string
    return <text x={x} y={y + 12} textAnchor="middle" fontSize={9} fill="#7a8499">{val}</text>;
  }

  if (visibleDays <= 14) {
    // Every day: day abbrev (highlighted on Monday) + numeric day below
    const dayNum = val ? val.split(' ')[0] : '';
    return (
      <g>
        <text x={x} y={y + 9} textAnchor="middle" fontSize={8}
          fontWeight={isMonday ? '700' : '400'}
          fill={isMonday ? '#d4a843' : '#5a6477'}>
          {DOW_SHORT[dow]}
        </text>
        <text x={x} y={y + 18} textAnchor="middle" fontSize={8} fill={isMonday ? '#9a8a60' : '#3a4255'}>
          {dayNum}
        </text>
      </g>
    );
  }

  // 15–60 days: Monday-only ticks — show "Mo" + full date
  return (
    <g>
      <text x={x} y={y + 9} textAnchor="middle" fontSize={8} fontWeight="700" fill="#d4a843">Mo</text>
      <text x={x} y={y + 18} textAnchor="middle" fontSize={8} fill="#7a8499">{val}</text>
    </g>
  );
}

function rollingAvg(values, window = 7) {
  return values.map((_, i) => {
    const start = Math.max(0, i - window + 1);
    const slice = values.slice(start, i + 1);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
}

const ZOOM_RANGE_STYLE = `
  .traj-range{position:absolute;inset:0;width:100%;-webkit-appearance:none;appearance:none;background:transparent;pointer-events:none;}
  .traj-range::-webkit-slider-thumb{-webkit-appearance:none;pointer-events:all;width:13px;height:13px;border-radius:50%;background:#d4a843;cursor:ew-resize;border:2px solid #13161b;box-shadow:0 0 0 1px #d4a84360;}
  .traj-range::-moz-range-thumb{pointer-events:all;width:11px;height:11px;border-radius:50%;background:#d4a843;cursor:ew-resize;border:2px solid #13161b;}
`;

export function ZoomBar({ zoomRange, setZoomRange, daily }) {
  if (!daily || daily.length <= 1) return null;
  const maxIdx = Math.max(0, daily.length - 1);
  const zStart = Math.min(zoomRange[0], maxIdx);
  const zEnd = Math.min(zoomRange[1], maxIdx);
  const isZoomed = zStart > 0 || zEnd < maxIdx;
  return (
    <div>
      <style>{ZOOM_RANGE_STYLE}</style>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-[#7a8499]">
          {isZoomed
            ? `${zEnd - zStart + 1} of ${daily.length} days · ${fmtDateFull(daily[zStart]?.day_date)} → ${fmtDateFull(daily[zEnd]?.day_date)}`
            : `All ${daily.length} days — drag handles to zoom`}
        </span>
        {isZoomed && (
          <button type="button" onClick={() => setZoomRange([0, 179])}
            className="text-[11px] text-[#7a8499] hover:text-[#d4a843] cursor-pointer bg-transparent border-none p-0 leading-none">
            Reset
          </button>
        )}
      </div>
      <div className="relative" style={{ height: 18 }}>
        <div className="absolute rounded"
          style={{ top: '50%', transform: 'translateY(-50%)', left: 0, right: 0, height: 3, background: '#1f2430' }} />
        <div className="absolute rounded"
          style={{
            top: '50%', transform: 'translateY(-50%)',
            left: `${(zStart / maxIdx) * 100}%`,
            right: `${((maxIdx - zEnd) / maxIdx) * 100}%`,
            height: 3, background: '#d4a84340',
          }} />
        <input type="range" className="traj-range" min={0} max={maxIdx} value={zStart}
          onChange={(e) => {
            const v = Math.min(Number(e.target.value), zEnd - 1);
            setZoomRange([v, zoomRange[1]]);
          }} />
        <input type="range" className="traj-range" min={0} max={maxIdx} value={zEnd}
          onChange={(e) => {
            const v = Math.max(Number(e.target.value), zStart + 1);
            setZoomRange([zoomRange[0], v]);
          }} />
      </div>
    </div>
  );
}

export default function TrajectoryChart({ daily, bonusTypes, zoomRange, setZoomRange }) {
  const [visible, setVisible] = useState({
    bet_count: false, stake_eur: true, ggr_eur: true,
    ngr3_eur: false, hold_eur: false, deposit_eur: false, cashout_eur: false, issued_bonus_eur: false,
  });
  const [smooth, setSmooth] = useState(0); // 0 = raw, 7 = 7d avg, 30 = 30d avg
  const [dateMode, setDateMode] = useState('created_at'); // 'created_at' | 'activated_at'
  const [overlayTitles, setOverlayTitles] = useState([]);
  const [overlaySearch, setOverlaySearch] = useState('');
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayHover, setOverlayHover] = useState(null); // { ol, x, y }
  const [stripHover, setStripHover] = useState(null);    // { row, seg, x, y }
  const overlayPanelRef = useRef(null);

  // Close the overlay dropdown when clicking outside
  useEffect(() => {
    if (!overlayOpen) return;
    function handleClickOutside(e) {
      if (overlayPanelRef.current && !overlayPanelRef.current.contains(e.target)) {
        setOverlayOpen(false);
        setOverlaySearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [overlayOpen]);

  const data = useMemo(() => {
    const rows = daily || [];
    const raw = rows.map((w) => {
      const row = {
        dayIdx: w.day_idx,
        week: fmtDay(w.day_date),
        bonus_value_eur: w.bonus_value_eur || 0,
        bonus_count: w.bonus_count || 0,
      };
      for (const s of SERIES) row[s.key] = w[s.key] || 0;
      return row;
    });
    const smoothedVals = smooth > 0
      ? Object.fromEntries(SERIES.map((s) => [s.key, rollingAvg(raw.map((r) => r[s.key]), smooth)]))
      : null;
    return raw.map((row, i) => {
      const warmup = smoothedVals && i < smooth - 1;
      return smoothedVals
        ? { ...row, ...Object.fromEntries(SERIES.map((s) => [s.key, warmup ? null : smoothedVals[s.key][i]])) }
        : row;
    });
  }, [daily, smooth]);

  const maxIdx = Math.max(0, data.length - 1);
  const zStart = Math.min(zoomRange[0], maxIdx);
  const zEnd = Math.min(zoomRange[1], maxIdx);
  const chartData = useMemo(() => data.slice(zStart, zEnd + 1), [data, zStart, zEnd]);

  const visibleDays = zEnd - zStart + 1;

  const { xTicks, dowMap } = useMemo(() => {
    const map = new Map(); // week string → day-of-week (0=Sun…6=Sat)
    chartData.forEach((row, i) => {
      const d = daily?.[zStart + i]?.day_date;
      if (d) map.set(row.week, new Date(d).getDay());
    });

    let ticks = [];
    if (visibleDays <= 14) {
      // Every day
      ticks = chartData.map((r) => r.week);
    } else if (visibleDays <= 60) {
      // Mondays only
      chartData.forEach((row, i) => {
        const d = daily?.[zStart + i]?.day_date;
        if (d && new Date(d).getDay() === 1) ticks.push(row.week);
      });
    } else {
      // Month starts
      let lastMonth = -1;
      chartData.forEach((row, i) => {
        const d = daily?.[zStart + i]?.day_date;
        if (d) {
          const m = new Date(d).getMonth();
          if (m !== lastMonth) { ticks.push(row.week); lastMonth = m; }
        }
      });
    }
    return { xTicks: ticks, dowMap: map };
  }, [chartData, daily, zStart, visibleDays]);

  // Max bonus value in the visible window — shown in the strip header.
  const stripMaxValue = useMemo(() => {
    if (!daily?.length || !bonusTypes?.length) return 0;
    const minT = new Date(daily[zStart].day_date).getTime();
    const maxT = new Date(daily[Math.min(zEnd, daily.length - 1)].day_date).getTime() + 86399999;
    return (bonusTypes || []).reduce((m, r) => {
      if (!r.created_at) return m;
      const t = new Date(r.created_at).getTime();
      return t >= minT && t <= maxT ? Math.max(m, r.amount_eur || 0) : m;
    }, 0);
  }, [bonusTypes, daily, zStart, zEnd]);

  // Alternating ISO week bands (Mon–Sun) rendered as ReferenceArea behind the lines.
  // Store week groups as {startI, endI, idx} so pixel rendering never needs a
  // category key that might be absent from chartData (e.g. an inactive Monday).
  const weekBands = useMemo(() => {
    if (!chartData.length || !daily) return [];
    const bands = [];
    let curKey = null;
    let startI = null;
    let bandIdx = 0;
    for (let i = 0; i < chartData.length; i++) {
      const d = daily[zStart + i];
      if (!d?.day_date) continue;
      const wk = isoWeekKey(d.day_date);
      if (wk !== curKey) {
        if (curKey !== null) {
          bands.push({ startI, endI: i - 1, idx: bandIdx++ });
        }
        curKey = wk;
        startI = i;
      }
    }
    if (startI !== null) {
      bands.push({ startI, endI: chartData.length - 1, idx: bandIdx });
    }
    return bands;
  }, [chartData, daily, zStart]);

  // Distinct bonus titles sorted busiest-first.
  const [overlaySortBy, setOverlaySortBy] = useState('count'); // 'count' | 'amount'

  const titleOptions = useMemo(() => {
    const map = new Map();
    for (const r of bonusTypes || []) {
      const label = r.title || r.bonus_type || 'unknown';
      const cur = map.get(label) || { label, count: 0, amount: 0 };
      map.set(label, { label, count: cur.count + 1, amount: cur.amount + (r.amount_eur || 0) });
    }
    return [...map.values()];
  }, [bonusTypes]);

  const filteredTitleOptions = useMemo(() => {
    const base = overlaySearch
      ? titleOptions.filter((t) => t.label.toLowerCase().includes(overlaySearch.toLowerCase()))
      : titleOptions;
    return [...base].sort((a, b) => overlaySortBy === 'count' ? b.count - a.count : b.amount - a.amount);
  }, [titleOptions, overlaySearch, overlaySortBy]);

  // One reference line per (day_idx, segment) pair — each group aggregates all
  // individual bonuses that land on the same day with the same status.
  const overlayLines = useMemo(() => {
    if (!overlayTitles.length || !data.length || !daily || !daily.length) return [];
    const sel = new Set(overlayTitles);
    const day0 = new Date(daily[0].day_date).getTime();
    // Map: `${idx}|${seg}` → { week, color, seg, bonuses: [] }
    const groups = new Map();
    for (const r of bonusTypes || []) {
      const label = r.title || r.bonus_type || 'unknown';
      if (!sel.has(label)) continue;
      const ts = dateMode === 'activated_at' ? r.activated_at : r.created_at;
      if (!ts) continue;
      const idx = Math.max(0, Math.min(data.length - 1,
        Math.floor((new Date(ts).getTime() - day0) / 86400000)));
      const seg = segmentOf(r);
      const key = idx + '|' + seg;
      let g = groups.get(key);
      if (!g) {
        g = { week: data[idx].week, color: STATUS_COLOR[seg], seg, bonuses: [] };
        groups.set(key, g);
      }
      g.bonuses.push({ label, date: ts, amount: r.amount_eur || 0, freespins: r.freespin_total || 0 });
    }
    return [...groups.values()].map((g) => {
      const totalAmount = g.bonuses.reduce((s, b) => s + b.amount, 0);
      const totalFs = g.bonuses.reduce((s, b) => s + b.freespins, 0);
      const shownTitles = [...new Set(g.bonuses.map((b) => b.label))];
      return {
        week: g.week,
        color: g.color,
        seg: g.seg,
        status: STATUS_LABEL[g.seg],
        bonusCount: g.bonuses.length,
        bonusValue: totalAmount,
        freespins: totalFs,
        date: g.bonuses[0].date,
        titles: shownTitles,
      };
    });
  }, [bonusTypes, overlayTitles, dateMode, data, daily]);

  function toggleTitle(label) {
    setOverlayTitles((prev) =>
      prev.includes(label) ? prev.filter((t) => t !== label) : [...prev, label],
    );
  }

  return (
    <div className="bg-[#13161b] border border-[#2a3042] rounded p-4">
      {/* Header row: title + series toggles */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <p className="text-sm font-bold text-[#d8dde8]">
          180-day trajectory
        </p>
        <div className="flex gap-1 flex-wrap items-center">
          {SERIES.map((s) => (
            <Toggle key={s.key} active={visible[s.key]} color={s.color}
              onClick={() => setVisible((v) => ({ ...v, [s.key]: !v[s.key] }))}>
              {s.label}
            </Toggle>
          ))}
          <div className="w-px bg-[#2a3042] mx-1 self-stretch" />
          <Toggle active={smooth === 7} onClick={() => setSmooth((v) => v === 7 ? 0 : 7)}>
            Smooth 7d
          </Toggle>
          <Toggle active={smooth === 30} onClick={() => setSmooth((v) => v === 30 ? 0 : 30)}>
            Smooth 30d
          </Toggle>
        </div>
      </div>

      {/* Bonus overlay selector with search filter */}
      {titleOptions.length > 0 && (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-[11px] text-[#7a8499]">Overlay bonus on chart:</span>

          {/* Date mode toggle */}
          <span className="text-[11px] text-[#7a8499]">Date:</span>
          {['created_at', 'activated_at'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setDateMode(m)}
              className="text-[10px] px-2 py-0.5 rounded border cursor-pointer select-none"
              style={{
                borderColor: dateMode === m ? '#d4a843' : '#2a3042',
                color: dateMode === m ? '#d4a843' : '#7a8499',
                backgroundColor: dateMode === m ? '#d4a84318' : 'transparent',
              }}
            >
              {m === 'created_at' ? 'Issued' : 'Activated'}
            </button>
          ))}

          {/* Custom multi-select popover */}
          <div className="relative" ref={overlayPanelRef}>
            <button
              type="button"
              onClick={() => setOverlayOpen((v) => !v)}
              className="text-[12px] text-[#d8dde8] bg-[#0d0f12] border border-[#2a3042] rounded px-2 py-1 min-w-[140px] text-left hover:border-[#d4a843] transition-colors"
            >
              {overlayTitles.length === 0
                ? 'None'
                : `${overlayTitles.length} bonus type${overlayTitles.length === 1 ? '' : 's'}`}
              <span className="float-right text-[#7a8499] ml-2">▾</span>
            </button>

            {overlayOpen && (
              <div className="absolute z-50 top-full left-0 mt-1 w-[300px] bg-[#1d2230] border border-white/10 rounded shadow-lg max-h-[380px] flex flex-col">
                {/* Search + sort */}
                <div className="p-1.5 border-b border-white/10 flex flex-col gap-1">
                  <input
                    type="text"
                    placeholder="Filter…"
                    value={overlaySearch}
                    onChange={(e) => setOverlaySearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="w-full bg-[#0d0f12] border border-[#2a3042] rounded px-2 py-1 text-[12px] text-[#d8dde8] placeholder-[#5a6477] outline-none focus:border-[#d4a843]"
                  />
                  <div className="flex gap-1">
                    <span className="text-[10px] text-[#5a6477] self-center mr-1">Sort:</span>
                    {['count', 'amount'].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOverlaySortBy(s); }}
                        className="text-[10px] px-2 py-0.5 rounded border cursor-pointer"
                        style={{
                          borderColor: overlaySortBy === s ? '#d4a843' : '#2a3042',
                          color: overlaySortBy === s ? '#d4a843' : '#7a8499',
                          backgroundColor: overlaySortBy === s ? '#d4a84318' : 'transparent',
                        }}
                      >
                        {s === 'count' ? 'By count' : 'By amount'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Options list */}
                <div className="overflow-y-auto flex-1">
                  {filteredTitleOptions.length === 0 ? (
                    <div className="text-[11px] text-[#5a6477] italic px-3 py-2">No matches</div>
                  ) : filteredTitleOptions.map(({ label, count, amount }) => (
                    <label
                      key={label}
                      className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-[#d8dde8] hover:bg-[#1f2430] cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={overlayTitles.includes(label)}
                        onChange={() => toggleTitle(label)}
                        className="accent-[#d4a843] shrink-0"
                      />
                      <span className="flex-1 truncate">{label}</span>
                      <span className="text-[10px] text-[#7a8499] shrink-0 tabular-nums">{count}×</span>
                      <span className="text-[10px] text-[#5a6477] shrink-0 tabular-nums">{fmtNum(amount)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {overlayTitles.length > 0 && (
            <button
              type="button"
              onClick={() => setOverlayTitles([])}
              className="text-[11px] text-[#7a8499] cursor-pointer select-none hover:text-[#d4a843] bg-transparent border-none p-0"
            >
              clear
            </button>
          )}
          <span className="text-[10px] text-[#5a6477]">
            ◆ = one mark per (day, status) group — hover for details
          </span>
        </div>
      )}

      {/* Bonus strip header — rendered as HTML above the chart */}
      {bonusTypes?.length > 0 && (
        <div className="flex justify-between items-center mb-1 flex-wrap gap-2">
          <p style={{ fontSize: 11, color: '#7a8499', margin: 0 }}>
            Bonus issuance — height = face value, colour = status, lined up with the days below.
          </p>
          <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
            {SEGMENTS.map((seg) => (
              <div key={seg} className="flex items-center" style={{ gap: 3 }}>
                <div style={{ width: 7, height: 7, borderRadius: '2px', backgroundColor: STATUS_COLOR[seg] }} />
                <span style={{ fontSize: 9, color: '#7a8499' }}>{STATUS_LABEL[seg]}</span>
              </div>
            ))}
            {stripMaxValue > 0 && (
              <span style={{ fontSize: 9, color: '#5a6477' }}>
                Tallest bar = {fmtNum(stripMaxValue)}
              </span>
            )}
          </div>
        </div>
      )}

      <ResponsiveContainer width="100%" height={bonusTypes?.length > 0 ? CHART_HEIGHT : 320}>
        <ComposedChart data={chartData} margin={bonusTypes?.length > 0 ? CHART_MARGIN : { top: 12, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="#1f2430" vertical={false} />
          <XAxis
            dataKey="week"
            ticks={xTicks}
            interval={0}
            tick={(props) => <CustomXTick {...props} dowMap={dowMap} visibleDays={visibleDays} />}
            tickLine={visibleDays <= 60 ? { stroke: '#2a3042', strokeWidth: 1 } : false}
            axisLine={{ stroke: '#2a3042' }}
            height={visibleDays <= 60 ? 28 : 18}
          />
          <YAxis
            tick={{ fontSize: 9, fill: '#7a8499' }} tickLine={false} axisLine={{ stroke: '#2a3042' }}
            tickFormatter={fmtNum} width={LEFT_AXIS_W}
          />
          <Tooltip content={<ChartTooltip visible={visible} />} />

          {/* Bonus strip bars — rendered inside the SVG so they share Recharts' exact scale */}
          {bonusTypes?.length > 0 && daily?.length > 0 && (
            <Customized component={({ xAxisMap, offset }) => {
              const xAxis = Object.values(xAxisMap || {})[0];
              if (!xAxis?.scale) return null;
              const sc = xAxis.scale;
              const bw = sc.bandwidth ? sc.bandwidth() : 0;
              const n = daily.length;
              const day0 = new Date(daily[0].day_date).getTime();
              const minT = new Date(daily[zStart].day_date).getTime();
              const maxT = new Date(daily[Math.min(zEnd, n - 1)].day_date).getTime() + 86399999;
              const vis = (bonusTypes || []).filter((r) => {
                if (!r.created_at) return false;
                const t = new Date(r.created_at).getTime();
                return t >= minT && t <= maxT;
              });
              const maxV = vis.reduce((m, r) => Math.max(m, r.amount_eur || 0), 0);
              const sy = offset.top - STRIP_H - (STRIP_GAP / 2); // strip top y in SVG coords
              return (
                <g>
                  <rect x={offset.left} y={sy} width={offset.width} height={STRIP_H}
                    fill="#0d0f12" onClick={() => setStripHover(null)} />
                  <line x1={offset.left} y1={sy + STRIP_H} x2={offset.left + offset.width} y2={sy + STRIP_H}
                    stroke="#2a3042" strokeWidth={1} />
                  {vis.map((r, i) => {
                    const bonusTs = new Date(r.created_at).getTime();
                    const idxFull = Math.max(0, Math.min(n - 1, Math.floor((bonusTs - day0) / 86400000)));
                    const idxInView = idxFull - zStart;
                    if (idxInView < 0 || idxInView > zEnd - zStart) return null;
                    const weekStr = chartData[idxInView]?.week;
                    if (weekStr === undefined || sc(weekStr) == null) return null;
                    const cx = sc(weekStr) + bw / 2;
                    const value = r.amount_eur || 0;
                    const norm = maxV > 0 ? Math.pow(value / maxV, 0.7) : 0;
                    const barH = Math.max(4, norm * (STRIP_H - 2));
                    const seg = segmentOf(r);
                    const isSelected = stripHover?.row === r;
                    return (
                      <rect key={i} x={cx - 1.5} y={sy + STRIP_H - barH} width={3} height={barH}
                        fill={STATUS_COLOR[seg]} rx={1}
                        opacity={stripHover && !isSelected ? 0.4 : 1}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setStripHover(isSelected ? null : { row: r, seg, x: e.clientX, y: e.clientY });
                        }}
                      />
                    );
                  })}
                </g>
              );
            }} />
          )}

          {/* Week bands — pixel-based so inactive-day gaps can't cause overflow */}
          <Customized component={({ xAxisMap, offset }) => {
            const xAxis = Object.values(xAxisMap || {})[0];
            if (!xAxis?.scale) return null;
            const sc = xAxis.scale;
            const bw = sc.bandwidth ? sc.bandwidth() : 0;
            const inner = weekBands.slice(1, -1).filter((b) => b.idx % 2 === 0);
            return (
              <g>
                {inner.map((b) => {
                  const x1 = sc(chartData[b.startI]?.week);
                  const x2 = sc(chartData[b.endI]?.week);
                  if (x1 == null || x2 == null) return null;
                  return (
                    <rect key={b.idx}
                      x={x1} y={offset.top}
                      width={x2 - x1 + bw} height={offset.height}
                      fill="#8899bb" fillOpacity={0.05}
                    />
                  );
                })}
              </g>
            );
          }} />

          {overlayLines.map((ol, i) => (
            <ReferenceLine
              key={i} x={ol.week} stroke={ol.color} strokeDasharray="3 3" strokeWidth={1.5}
              label={(props) => (
                <OverlayMarker
                  {...props}
                  ol={ol}
                  isSelected={overlayHover?.ol === ol}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOverlayHover((prev) => prev?.ol === ol ? null : { ol, x: e.clientX, y: e.clientY });
                  }}
                />
              )}
            />
          ))}

          {SERIES.filter((s) => visible[s.key]).map((s) => (
            <Line key={s.key} type="monotone" dataKey={s.key}
              stroke={s.color} strokeWidth={2} dot={false} isAnimationActive={false} />
          ))}

        </ComposedChart>
      </ResponsiveContainer>


      {overlayTitles.length > 0 && (
        <div className="flex gap-3 flex-wrap mt-1">
          {SEGMENTS.map((seg) => (
            <div key={seg} className="flex items-center gap-1">
              <div style={{ width: 10, height: 0, borderTop: `2px dashed ${STATUS_COLOR[seg]}` }} />
              <span className="text-[9px] text-[#7a8499]">{STATUS_LABEL[seg]}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] text-[#5a6477] mt-2 italic">
        Zero days are real days, not gaps. Bonuses near a trajectory change may be a cause or a
        response — retention bonuses are often given to players who are already declining.
      </p>

      {/* Fixed-position click tooltip for strip bars */}
      {stripHover && (
        <div
          style={{
            position: 'fixed',
            left: stripHover.x + 14,
            top: stripHover.y - 12,
            zIndex: 9999,
            maxWidth: 240,
            boxShadow: '0 4px 12px rgba(0,0,0,0.6)',
            cursor: 'default',
          }}
          className="bg-[#0d0f12] border border-[#2a3042] rounded p-3"
          onClick={() => setStripHover(null)}
        >
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="text-[11px] font-bold" style={{ color: STATUS_COLOR[stripHover.seg] }}>
              {stripHover.row.title || stripHover.row.bonus_type || '—'}
            </div>
            <button type="button" onClick={() => setStripHover(null)}
              className="text-[#5a6477] hover:text-[#d8dde8] text-[12px] leading-none bg-transparent border-none p-0 cursor-pointer shrink-0">
              ×
            </button>
          </div>
          <div className="text-[11px]" style={{ color: STATUS_COLOR[stripHover.seg] }}>
            {STATUS_LABEL[stripHover.seg]}
          </div>
          <div className="text-[11px] text-[#7a8499]">
            Created: {fmtDateFull(stripHover.row.created_at)}
          </div>
          {stripHover.row.strategy && (
            <div className="text-[10px] text-[#5a6477]">Strategy: {stripHover.row.strategy}</div>
          )}
          <div className="text-[11px] text-[#7a8499] mt-1">
            €{fmtNum(stripHover.row.amount_eur)}
            {stripHover.row.freespin_total > 0 && ` · ${stripHover.row.freespin_total.toLocaleString('en-GB')} FS`}
          </div>
        </div>
      )}

      {/* Fixed-position click tooltip for overlay markers */}
      {overlayHover && (
        <div
          style={{
            position: 'fixed',
            left: overlayHover.x + 14,
            top: overlayHover.y - 12,
            zIndex: 9999,
            maxWidth: 260,
            boxShadow: '0 4px 12px rgba(0,0,0,0.6)',
            cursor: 'default',
          }}
          className="bg-[#0d0f12] border border-[#2a3042] rounded p-3"
          onClick={() => setOverlayHover(null)}
        >
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="text-[11px] font-bold" style={{ color: overlayHover.ol.color }}>
              {overlayHover.ol.status}
            </div>
            <button type="button" onClick={() => setOverlayHover(null)}
              className="text-[#5a6477] hover:text-[#d8dde8] text-[12px] leading-none bg-transparent border-none p-0 cursor-pointer shrink-0">
              ×
            </button>
          </div>
          <div className="text-[11px] text-[#7a8499] mb-1">
            {dateMode === 'activated_at' ? 'Activated' : 'Created'}: {fmtDateFull(overlayHover.ol.date)}
          </div>
          {overlayHover.ol.titles && overlayHover.ol.titles.slice(0, 3).map((t) => (
            <div key={t} className="text-[11px] text-[#d8dde8] truncate">{t}</div>
          ))}
          {overlayHover.ol.titles && overlayHover.ol.titles.length > 3 && (
            <div className="text-[10px] text-[#5a6477]">+{overlayHover.ol.titles.length - 3} more</div>
          )}
          <div className="text-[11px] text-[#7a8499] mt-1">
            {overlayHover.ol.bonusCount} bonus{overlayHover.ol.bonusCount !== 1 ? 'es' : ''} · €{fmtNum(overlayHover.ol.bonusValue)}
          </div>
          {overlayHover.ol.freespins > 0 && (
            <div className="text-[11px] text-[#7a8499]">
              {overlayHover.ol.freespins.toLocaleString('en-GB')} free spins
            </div>
          )}
        </div>
      )}
    </div>
  );
}
